import { Row, Col } from 'react-bootstrap';
import './styles.css';
import AdditionalField from './AdditionalField';
import {
  AdditionalSectionKeys,
  type AdditionalSectionValue,
  type SectionField,
  PatientEpisodeDetailsNames,
  PMMDocumentTypes,
} from './types';
import { useCallback, useMemo } from 'react';
import {
  getEGFRTrend,
  mapToMedProfileValue,
  mapToPmmDocumentValue,
  mapToPrevAdmissionValue,
} from './utils';
import { type PMMNameTypes } from '../../../Table/components/CareProgress/types';
import {
  calculateDateGap,
  convertToFormattedDateString,
} from '../../../../helpers';
import { usePatientProfile } from '../../hooks';
import useLatestEpisode from '../../../../hooks/useLatestEpisode';
import { useMedicationProfilesShared } from '../../context/MedicationProfilesProvider';

// Function to reorder the array
function reorderSectionArrays(array: string[]) {
  const desiredOrder = [
    AdditionalSectionKeys.ADMISSION_DETAILS,
    AdditionalSectionKeys.MEDICATION_DETAILS,
    AdditionalSectionKeys.CARE_PROGRESS,
  ];

  return desiredOrder.filter((item) => array.includes(item));
}

interface AdditionalSectionProps {
  config: {
    [key in AdditionalSectionKeys]: SectionField[];
  };
  patientProfile: any;
  onInputChange: (argu: any) => void;
}

export const AdditionalSection = ({
  config,
  patientProfile,
  onInputChange,
}: AdditionalSectionProps) => {
  const { editProfile } = usePatientProfile();
  const { latestEpisode } = useLatestEpisode();
  const { medProfiles } = useMedicationProfilesShared();
  const types = useMemo(() => (config ? Object.keys(config) : []), [config]);
  const colWidth = useMemo(
    () => (types.length ? Math.floor(12 / types.length) : 0),
    [types.length],
  );
  const getValueForField = useCallback(
    (
      key: PatientEpisodeDetailsNames | PMMNameTypes,
    ): AdditionalSectionValue => {
      const details: any[] = patientProfile.usr_episodes_details;

      if (PMMDocumentTypes.includes(key)) {
        if (key === 'Med. profile') {
          const mapped = mapToMedProfileValue(
            medProfiles,
            patientProfile,
            details,
          );
          return { value: [...mapped] };
        }
        return {
          value: mapToPmmDocumentValue(key as PMMNameTypes, details),
        };
      }

      if (key === PatientEpisodeDetailsNames.EGFR) {
        const filteredDetails = [...details]
          .sort(
            (a: any, b: any) =>
              new Date(b.event_date).getTime() -
              new Date(a.event_date).getTime(),
          )
          .filter(
            (detail) =>
              detail.name === key ||
              detail.name === '.Estimated Glomerular Filtration Rate',
          );
        const latestValue =
          filteredDetails[0]?.detail?.fhir?.valueQuantity?.value || null;
        const trend = getEGFRTrend(filteredDetails) || 0;
        return {
          value: latestValue,
          trend,
          dateStr: filteredDetails[0]?.event_date
            ? convertToFormattedDateString(
                filteredDetails[0]?.event_date,
                'D MMM YY',
              )
            : '',
        };
      }
      const detail = details.find((detail) => detail.name === key);

      if (key === PatientEpisodeDetailsNames.PrevAdmission) {
        const value = mapToPrevAdmissionValue(detail?.value);
        return { value };
      }

      if (key === PatientEpisodeDetailsNames.MedicationDiet) {
        if (Array.isArray(detail?.value)) {
          return { value: (detail?.value as string[]).join(', ') };
        }
        return { value: detail?.value || '' };
      }

      if (key === PatientEpisodeDetailsNames.RouteModifiers) {
        if (Array.isArray(detail?.value)) {
          return { value: (detail?.value as string[]).join(', ') };
        }
        return { value: detail?.value || '' };
      }

      if (key === PatientEpisodeDetailsNames.LengthOfStay) {
        const dateAdmitted = details.find(
          (detail) => detail.name === PatientEpisodeDetailsNames.DateAdmitted,
        );
        if (!dateAdmitted?.value) {
          return { value: '' };
        }
        return {
          value:
            calculateDateGap(dateAdmitted.value, new Date().toISOString()) ||
            '',
        };
      }

      if (key === PatientEpisodeDetailsNames.DateAdmitted) {
        const admissionDate = convertToFormattedDateString(
          latestEpisode?.episode_opened_date || '',
        );
        return { value: admissionDate };
      }

      return {
        value: detail?.value ?? '',
      };
    },
    [
      patientProfile.usr_episodes_details,
      medProfiles,
      latestEpisode?.episode_opened_date,
    ],
  );

  return (
    <>
      <Row className="position-relative pt-4 px-3">
        <Row id="additional-section">
          {reorderSectionArrays(types).map((type, index) => (
            <Col
              lg={colWidth}
              key={`column-${index}`}
              className={`${type.toLowerCase().replace(/ /g, '-')} standard-font-size-pdf`}
            >
              <h5 className="patient-footer-header">{type}</h5>
              {config &&
                config[type].map((field, index) => (
                  <AdditionalField
                    key={`${field.key}-${index}`}
                    field={
                      field.title ===
                      PatientEpisodeDetailsNames.EstimatedGlomerularFiltrationRate
                        ? { ...field, title: 'eGFR' }
                        : field
                    }
                    value={getValueForField(
                      field.title === 'Admission reason'
                        ? PatientEpisodeDetailsNames.AdmissionReason
                        : field.title ===
                            PatientEpisodeDetailsNames.EstimatedGlomerularFiltrationRate
                          ? PatientEpisodeDetailsNames.EGFR
                          : (field.title as PatientEpisodeDetailsNames),
                    )}
                    editProfile={editProfile}
                    onInputChange={onInputChange}
                  />
                ))}
            </Col>
          ))}
        </Row>
      </Row>
    </>
  );
};
